import { Box, Button, CircularProgress, Grid, TextField, Typography } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import { useInfo, useUpdateInfo } from "../../hooks/useInfo";
import toast from "react-hot-toast";
import RichTextEditor from "../RichTextEditor";

const darkFieldSx = {
  "& .MuiInputBase-root": { color: "rgba(255,255,255,0.9)" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.45)" },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.6)" },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 100px #0a0a0a inset",
      WebkitTextFillColor: "rgba(255,255,255,0.9)",
      caretColor: "white",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "rgba(255,255,255,0.8)" },
};

export default function ContactForm() {
  const { data: info, isLoading } = useInfo();
  const updateInfoMutation = useUpdateInfo();

  const [state, setState] = useState({
    description: "",
    facebook: "",
    instagram: "",
    soundcloud: "",
    email: "",
    phoneNumber: "",
  });

  useEffect(() => {
    if (info) {
      setState(info);
    }
  }, [info]);

  const handleChange = useCallback((key, value) => {
    setState((prevState) => ({ ...prevState, [key]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      await updateInfoMutation.mutateAsync(state);
      toast.success("Contact information updated successfully");
    } catch {
      toast.error("Failed to update contact information");
    }
  }, [state, updateInfoMutation]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "rgba(255,255,255,0.35)" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography
        variant="overline"
        sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: "0.2em", fontSize: "0.68rem" }}
      >
        Site Information
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="E-mail"
            value={state.email}
            fullWidth
            sx={darkFieldSx}
            onChange={(e) => handleChange("email", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone Number"
            value={state.phoneNumber}
            fullWidth
            sx={darkFieldSx}
            onChange={(e) => handleChange("phoneNumber", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Facebook"
            value={state.facebook}
            fullWidth
            sx={darkFieldSx}
            onChange={(e) => handleChange("facebook", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Instagram"
            value={state.instagram}
            fullWidth
            sx={darkFieldSx}
            onChange={(e) => handleChange("instagram", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Soundcloud"
            value={state.soundcloud}
            fullWidth
            sx={darkFieldSx}
            onChange={(e) => handleChange("soundcloud", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <RichTextEditor
            label="About Us Description"
            value={state.description}
            onChange={(value) => handleChange("description", value)}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button
          onClick={handleSubmit}
          variant="outlined"
          disabled={updateInfoMutation.isPending}
          sx={{
            borderColor: "rgba(255,255,255,0.22)",
            color: "rgba(255,255,255,0.8)",
            px: 4,
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            "&:hover": {
              borderColor: "white",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: "white",
            },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.2)",
              borderColor: "rgba(255,255,255,0.08)",
            },
          }}
        >
          {updateInfoMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
